import { useState, useMemo, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Typography, Link, Tooltip, Box, Stack, Menu, MenuItem, IconButton, Divider } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useSelector, useDispatch } from '../../../../redux/store';
import Scrollbar from '../../../../reusable-components/scrollbar';
import Iconify from '../../../../reusable-components/iconify';
import LogSearchBar from '../../../../reusable-components/log-search-bar/LogSearchBar';
import { useLogSearch, createSearchToggleHandler } from '../../../../reusable-components/log-search-bar/logSearchUtils';
import LogSearchEntry from '../../../../reusable-components/log-search-bar/LogSearchEntry';
import { enqueueSnackbar as enqueueGlobalSnackbar, useSnackbar } from '../../../../reusable-components/snackbar';
import DropdownAction from '../../../../reusable-components/dropdown-action/DropdownAction';
import DropdownExpandableAction from '../../../../reusable-components/dropdown-action/DropdownExpandableAction';
import { addInvoiceFile, renderInvoiceFile, getInvoiceFiles } from '../../../../redux/slices/invoicing';
import { InvoiceAttachment } from './InvoiceAttachments';
import { generateInvoicePdfFromData, getInvoiceLogFormatted } from '../../../../api/invoicing';
import { fMonthDayYearShort } from '../../../../utils/formatTime';
import LogTemplate from '../../../../reusable-components/activity-log/LogTemplate';
import InvoiceStatusModalButton from '../InvoiceStatusModalButton';
import { BookingFailureType, InvoiceAttachmentCategories } from '../../../../utils/globalEnums';
import { HOST_API_URL } from '../../../../config-global';
import { useIsInvoiceEditable } from './reusable/hooks/useInvoiceLocks';
import { escapeHtml } from '../../../../reusable-components/markdown/markdown-cleanup';
import { printHtml } from '../../../../utils/printBlob';

const SEARCH_ICON_MIN_WIDTH = 270;

// Cached across calls/instances since the logo is a static asset that never changes -
// avoids re-fetching and re-encoding it as a data URI on every Print click.
let logoDataUrlPromise = null;
const getLogoDataUrl = () => {
  if (!logoDataUrlPromise) {
    logoDataUrlPromise = fetch('/assets/logo.png')
      .then((response) => {
        // fetch only rejects on network errors - route HTTP errors (404/500) into the
        // catch below so an error page isn't cached and embedded as the logo.
        if (!response.ok) throw new Error(`Logo fetch failed: ${response.status}`);
        return response.blob();
      })
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .catch(() => {
        // Don't cache a failure - a transient network hiccup shouldn't permanently
        // blank the logo on every Print click for the rest of the tab's life.
        logoDataUrlPromise = null;
        return '';
      });
  }
  return logoDataUrlPromise;
};

export const InvoiceLog = ({ tabs, setHighlightItem, setSelectedFileId, logHeight = 400 }) => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [expand, setExpand] = useState(true);
  const [filterType, setFilterType] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNarrow, setIsNarrow] = useState(false);
  const containerRef = useRef(null);
  const {
    data: { invoiceLogData, invoiceFiles, invoiceItemData, invoiceGLItems },
  } = useSelector((state) => state.invoicing);
  const { isEditable } = useIsInvoiceEditable(true);
  const dispatch = useDispatch();
  const fileArrayWithType = useMemo(() => {
    return (invoiceFiles || []).map((f) => ({
      ...f,
      messageType: 'File',
    }));
  }, [invoiceFiles]);
  const mergedArray = useMemo(() => {
    return [...(invoiceLogData || []), ...(fileArrayWithType || [])].sort(
      (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
    );
  }, [invoiceLogData, fileArrayWithType]);
  const intacctDataFile = invoiceFiles
    .filter((f) => f.invoiceAttachmentCategoryId === InvoiceAttachmentCategories.IntacctDataFile)
    .sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded))[0]; //take most recent "data sent to intacct"

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);

  const handleActionsClick = (event) => {
    setExpand(true);
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setIsNarrow(entry.contentRect.width < SEARCH_ICON_MIN_WIDTH);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearchToggle = createSearchToggleHandler(setShowSearchBar, setSearchQuery, () => setExpand(true));

  const handleToggleExpand = () => {
    setExpand((prevExpand) => {
      const next = !prevExpand;
      if (!next) {
        setShowSearchBar(false);
        setSearchQuery('');
      }
      return next;
    });
  };

    const handleLogDownload = () => {
        window.open(`${HOST_API_URL}Api/Invoices/DownloadInvoiceLog?invoiceId=${id}`, '_blank');
    };

    const handleLogPrint = async () => {
      // Fetches server-formatted rows (same Action/Description logic DownloadInvoiceLog
      // uses for the Excel export) rather than re-deriving the formatting in JS, so Print
      // and Download can't silently drift apart.
      let rows;
      let logoDataUrl;
      try {
        const [response, logo] = await Promise.all([getInvoiceLogFormatted(id), getLogoDataUrl()]);
        rows = response.data || [];
        logoDataUrl = logo;
      } catch {
        enqueueSnackbar('Failed to load invoice log for printing', { variant: 'error' });
        return;
      }

      const vendor = invoiceItemData?.invoice?.vendor || '';
      const generatedDate = fMonthDayYearShort(new Date());

      const tableRows = rows
        .map(
          (row) => `
            <tr>
              <td>${escapeHtml(row.action)}</td>
              <td>${escapeHtml(row.description)}</td>
              <td>${escapeHtml(row.attachments)}</td>
              <td>${escapeHtml(row.updatedAt)}</td>
              <td>${escapeHtml(row.updatedBy)}</td>
            </tr>`
        )
        .join('');

      const metaBox = (label, value, { wide = false } = {}) => `
        <div class="meta-box${wide ? ' wide' : ''}"><div class="meta-label">${escapeHtml(
        label
      )}</div><div class="meta-value">${escapeHtml(value)}</div></div>`;

      const html = `
        <html>
          <head>
            <title>${escapeHtml(`Invoice Log #${id}`)}</title>
            <style>
              * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
              body { margin: 0; font-family: "Roboto", sans-serif; color: #212B36; padding: 24px; }
              .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
              .brand img { max-width: 120px; width: 100%; display: block; }
              .title { font-family: Georgia, "Times New Roman", serif; font-size: 30px; font-weight: 700; color: #233340; line-height: 1.15; text-align: right; align-self: center; }
              .meta { display: flex; flex-direction: column; gap: 4px; align-items: stretch; }
              .meta-row { display: flex; gap: 4px; }
              .meta-box { border: 1px solid #233340; min-width: 90px; }
              .meta-box.wide { width: 100%; }
              .meta-label { background-color: #233340; color: rgba(255, 255, 255, 0.75); font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 3px 8px; }
              .meta-value { padding: 4px 8px; font-size: 12px; font-weight: 600; }
              .bar { display: flex; align-items: stretch; border: 1px solid #233340; margin-bottom: 16px; }
              .bar-label { background-color: #233340; color: rgba(255, 255, 255, 0.75); font-size: 10px; font-weight: 700; text-transform: uppercase; padding: 6px 12px; display: flex; align-items: center; }
              .bar-value { padding: 6px 12px; font-size: 12px; flex: 1; display: flex; align-items: center; }
              table { border: 0.5px solid #233340; border-collapse: collapse; width: 100%; }
              thead { display: table-header-group; }
              tr { break-inside: avoid; page-break-inside: avoid; }
              th { background-color: #233340; color: rgba(255, 255, 255, 0.75); text-transform: uppercase; font-size: 9px; padding: 5px; text-align: left; white-space: nowrap; }
              td { border: 0.5px solid #233340; border-width: 0 0 0.5px 0.5px; font-size: 8px; padding: 4px 5px 3px; vertical-align: top; }
              .footer { display: flex; margin-top: 16px; }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="brand">
                ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Platform Solutions" />` : ''}
              </div>
              <div class="title">Invoice Log</div>
              <div class="meta">
                <div class="meta-row">
                  ${metaBox('Invoice #', id)}
                  ${metaBox('Entries', rows.length)}
                </div>
                ${metaBox('Generated', generatedDate, { wide: true })}
              </div>
            </div>
            ${vendor ? `<div class="bar"><div class="bar-label">Vendor</div><div class="bar-value">${escapeHtml(vendor)}</div></div>` : ''}
            <table>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Description</th>
                  <th>Attachments</th>
                  <th>Updated At</th>
                  <th>Updated By</th>
                </tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>
            <div class="footer">
              ${metaBox('Generated', generatedDate)}
            </div>
          </body>
        </html>`;

      printHtml(html);
    };

  const { open: openDropzone } = useDropzone({
    accept: {
      'application/pdf': [],
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0] === undefined) return;
      addFile(acceptedFiles[0]);
    },
  });

  const addFile = async (file) => {
    const response = await dispatch(addInvoiceFile(id, file));
    dispatch(renderInvoiceFile(id));
    if (response === 'success') {
      enqueueSnackbar('File added successfully', { variant: 'success' });
    }
  };

  const styleDate = (dateStr) => {
    const date = new Date(dateStr);
    const formattedDate = `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${fMonthDayYearShort(
      date
    )} ${date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })}`;
    return (
      <Tooltip title={formattedDate}>
        <Typography
          variant="subtitle2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'noWrap',
          }}
        >
          {formattedDate} EST
        </Typography>
      </Tooltip>
    );
  };

  const generatePDF = async () => {
    try {
      const response = await generateInvoicePdfFromData(id);
      if (response.status === 200) {
        enqueueSnackbar('PDF generated successfully', { variant: 'success' });
        await dispatch(getInvoiceFiles(id));
      }
    } catch (error) {
      enqueueSnackbar('PDF generation failed', { variant: 'error' });
    }
  };

  const getHtmlMessage = (logEntry, setHighlightItem) => {
    let extraInfo;
    try {
      const jsonMessageTypes = ['ItemUpdate', 'NewInvoice', 'BookingFailed'];
      extraInfo = jsonMessageTypes.includes(logEntry?.messageType) ? JSON.parse(logEntry.extraInfo) : {};
    } catch {
      extraInfo = {};
    }

    const sentBy = extraInfo?.sentBy;
    const invoiceAddedHtml = (
      <>
        {' '}
        {logEntry?.user?.id === '0' ? (
          invoiceItemData?.invoice?.sourceMethod === 'Manual' ? (
            <Stack>
              <Typography
                color="info.main"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {`Invoice was added via email`}
              </Typography>
              {sentBy && (
                <Tooltip title={`${sentBy}`}>
                  <Typography
                    color="info.secondary"
                    sx={{
                      fontSize: '14px',
                      paddingRight: '2px',
                      alignContent: 'center',
                      maxWidth: '250px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {`From: ${sentBy}`}
                  </Typography>
                </Tooltip>
              )}
              <Tooltip title={`${logEntry?.user?.fullName}`}>
                <Typography
                  color="info.secondary"
                  sx={{
                    fontSize: '14px',
                    paddingRight: '2px',
                    alignContent: 'center',
                    maxWidth: '250px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {`To: ${logEntry?.user?.fullName}`}
                </Typography>
              </Tooltip>
            </Stack>
          ) : (
            <Stack>
              <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, paddingRight: '2px' }}>
                {`Invoice was uploaded automatically`}
              </Typography>
              <Tooltip title={logEntry?.user?.fullName}>
                <Typography
                  color="info.secondary"
                  sx={{
                    fontSize: '14px',
                    paddingRight: '2px',
                    alignContent: 'center',
                    maxWidth: '250px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {logEntry?.user?.fullName}
                </Typography>
              </Tooltip>
            </Stack>
          )
        ) : (
          <Typography color="info.main" sx={{ fontSize: '14px', paddingRight: '2px', fontWeight: 600 }}>
            Invoice was added by <CopyTooltip user={logEntry?.user} />
          </Typography>
        )}
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            justifyContent: 'left',
            alignItems: 'center',
            paddingRight: '25px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );

    const invoiceViewedHtml = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, paddingRight: '2px' }}>
          Invoice was viewed by <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            justifyContent: 'left',
            alignItems: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );

    let bookingFailedHtml = null;
    if (extraInfo?.failureType === BookingFailureType.BookingVendor) {
      // Truncate message if too long
      const maxMessageLength = 80;
      let displayMessage = extraInfo?.message;
      if (displayMessage && displayMessage.length > maxMessageLength) {
        displayMessage = `${displayMessage.substring(0, maxMessageLength)}...`;
      }
      bookingFailedHtml = (
        <>
          <Tooltip title={`Submitted by ${logEntry?.user?.fullName || 'Unknown'}. ${extraInfo?.message}`}>
            <span style={{ cursor: 'pointer' }}>
              <Typography
                component="span"
                color="info.main"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  paddingRight: '2px',
                  display: 'inline',
                }}
              >
                Booking Failed
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: '14px',
                  color: '#212B36',
                  fontWeight: 400,
                  paddingLeft: '8px',
                  display: 'inline',
                }}
              >
                {displayMessage}
              </Typography>
            </span>
          </Tooltip>
          <Typography
            sx={{
              display: 'flex',
              color: '#212B36',
              marginTop: '5px',
              justifyContent: 'left',
              alignItems: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '14px',
            }}
          >
            {styleDate(logEntry?.dateAdded)}
          </Typography>
        </>
      );
    }

    const invoiceRejectedHtml = (
      <>
        <Tooltip title={logEntry.extraInfo}>
          <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, display: 'inline' }}>
            Invoice was rejected by
          </Typography>
        </Tooltip>
        <Typography
          color="info.main"
          sx={{ fontSize: '14px', paddingRight: '2px', fontWeight: 600, display: 'inline' }}
        >
          {' '}
          <CopyTooltip user={logEntry?.user} />{' '}
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            paddingRight: '2px',
            justifyContent: 'left',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageUpdateApprovers = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', paddingRight: '2px', fontWeight: 600 }}>
          Approvers updated by <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            paddingRight: '2px',
            justifyContent: 'left',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageInvoiceApproval = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', paddingRight: '2px', fontWeight: 600 }}>
          Invoice was approved by <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            paddingRight: '2px',
            justifyContent: 'left',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageSubmissions = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', paddingRight: '2px', fontWeight: 600 }}>
          <InvoiceStatusModalButton
            id={invoiceItemData?.invoice?.id}
            total={invoiceItemData?.invoice?.total}
            vendor={invoiceItemData?.invoice?.vendor}
            bookingVendorStatus={invoiceItemData?.invoice?.bookingVendorStatus}
            inline
          />
          {logEntry.extraInfo?.split('||').filter(Boolean)?.length > 1 ? (
            <>
              {'Multi facility invoice'} was submitted by <CopyTooltip user={logEntry?.user} /> to:
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {logEntry.extraInfo
                  ?.split('||')
                  .filter(Boolean)
                  .map((item, i) => (
                    <li key={i} style={{ lineHeight: 1.4 }}>
                      {item}
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            <>
              {'Invoice'} was submitted by <CopyTooltip user={logEntry?.user} /> to{' '}
              <span>{logEntry.extraInfo?.split('||')?.[0]}</span>
            </>
          )}
        </Typography>
        {logEntry.extraInfo?.includes('Intacct') && intacctDataFile && (
          <InvoiceAttachment file={intacctDataFile} hasEditAccess={isEditable} setSelectedFileId={setSelectedFileId} />
        )}
        <Typography
          style={{
            display: 'flex',
            justifyContent: 'left',
            color: '#212B36',
            marginTop: '5px',
            paddingRight: '2px',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );

    const formName = extraInfo?.formName?.replace(/\s+/g, '')?.toLowerCase();
    const deletedItem =
      formName === 'invoiceinfo'
        ? false
        : !(formName === 'glcodes' || formName === 'glcodessummary'
            ? invoiceGLItems?.some((x) => x.glItemId === extraInfo.itemId)
            : formName === 'electronicinvoice'
            ? invoiceItemData?.invoiceItems?.some((x) => x.id === extraInfo?.itemId)
            : false);
    const viewItem = (extraInfo?.itemId !== 0 || formName === 'invoiceinfo') && !deletedItem;
    const splittedDescription = extraInfo?.description?.split('\n');
    const htmlMessageUpdate = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600 }}>
          {formName === 'electronicinvoice' ? 'Invoice Details was updated by' : 'Invoice was updated by'}{' '}
          <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Box>
          <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              {splittedDescription?.map(
                (line, index) =>
                  line !== 'Electronic Invoice updated:' &&
                  line !== 'Invoice Info updated:' &&
                  line !== '' && (
                    <li key={index} style={{ marginBottom: '2px' }}>
                      {viewItem ? (
                        <Link
                          color="inherit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setHighlightItem({ id: extraInfo.itemId, form: formName, description: line });
                          }}
                          sx={{
                            fontSize: '12px',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            display: 'inline',
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'text.primary',
                            },
                          }}
                        >
                          {line}
                        </Link>
                      ) : (
                        line
                      )}
                    </li>
                  )
              )}
            </ul>
          </Typography>
        </Box>
        <Typography
          style={{
            display: 'flex',
            justifyContent: 'center',
            color: '#212B36',
            marginTop: '5px',
            alignItems: 'top',
            width: 'fit-content',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageDelete = (
      <>
        <Typography
          color="info.main"
          sx={{
            fontSize: '14px',
            paddingRight: '2px',
            fontWeight: 600,
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: 'normal',
          }}
        >
          {logEntry?.extraInfo?.includes('Item on GL Codes form was deleted')
            ? logEntry?.extraInfo?.substring(0, 33)
            : logEntry?.extraInfo?.includes('Item on Electronic Invoice was deleted')
            ? 'Item on Invoice Details was deleted'
            : logEntry?.extraInfo?.substring(0, 25)}{' '}
          by <CopyTooltip user={logEntry?.user} />
        </Typography>

        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
          {logEntry?.extraInfo?.includes('Item on GL Codes form was deleted')
            ? logEntry?.extraInfo?.substring(34, logEntry?.extraInfo?.length)
            : logEntry?.extraInfo?.includes('Item on Electronic Invoice was deleted')
            ? logEntry?.extraInfo?.substring(39, logEntry?.extraInfo?.length)
            : ''}
        </Typography>
        <Typography
          style={{
            display: 'flex',
            paddingRight: '2px',
            justifyContent: 'left',
            color: '#212B36',
            marginTop: '5px',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageFile = (
      <>
        <Typography
          color="info.main"
          sx={{
            fontSize: '14px',
            paddingRight: '2px',
            overflow: 'hidden',
            fontWeight: 600,
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: 'normal',
          }}
        >
          File was added by {logEntry?.enteredBy}
        </Typography>
        <InvoiceAttachment file={logEntry} hasEditAccess={isEditable} setSelectedFileId={setSelectedFileId} />
        <Typography
          style={{
            display: 'flex',
            paddingRight: '2px',
            justifyContent: 'left',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageDownload = (
      <>
        <Typography
          color="info.main"
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            paddingRight: '2px',
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: 'normal',
          }}
        >
          Invoice was downloaded by <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            paddingRight: '2px',
            justifyContent: 'left',
            color: '#212B36',
            marginTop: '5px',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageMarkAsSent = (
      <>
        <Typography
          color="info.main"
          sx={{
            fontSize: '14px',
            paddingRight: '2px',
            fontWeight: 600,
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: 'normal',
          }}
        >
          Invoice already exists in Intacct. Automatically marked as Processed by <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingRight: '2px',
            color: '#212B36',
            marginTop: '5px',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageUpdateOrdersOnly = (
      <>
        <Typography
          color="info.main"
          sx={{
            fontSize: '14px',
            paddingRight: '2px',
            fontWeight: 600,
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: 'normal',
          }}
        >
          Invoice was automatically moved to processed
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
          The account is marked as update orders only
        </Typography>
        <Typography
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingRight: '2px',
            color: '#212B36',
            marginTop: '5px',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageNoChargeMarkedSent = (
      <>
        <Typography
          color="info.main"
          sx={{
            fontSize: '14px',
            paddingRight: '2px',
            fontWeight: 600,
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: 'normal',
          }}
        >
          No charge. Marked as processed by <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            justifyContent: 'left',
            paddingRight: '2px',
            color: '#212B36',
            marginTop: '5px',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageSubmitReview = (
      <>
        <Typography
          color="info.main"
          sx={{
            fontSize: '14px',
            paddingRight: '2px',
            fontWeight: 600,
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: 'normal',
          }}
        >
          Invoice was submitted for review by <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            justifyContent: 'left',
            paddingRight: '2px',
            color: '#212B36',
            marginTop: '5px',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageSubmitAnyway = (
      <>
        <Typography
          color="info.main"
          sx={{
            fontSize: '14px',
            paddingRight: '2px',
            fontWeight: 600,
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: 'normal',
          }}
        >
          <CopyTooltip user={logEntry?.user} /> submitted the invoice despite
          {logEntry?.extraInfo?.startsWith('Duplicate Warnings:')
            ? ' potential duplicate warnings.'
            : ' a potential duplicate warning.'}
          {logEntry?.extraInfo && (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{logEntry.extraInfo}</span>} placement="top" arrow>
              {' '}
              <span style={{ marginLeft: '6px', cursor: 'pointer' }}>
                <Iconify
                  icon="mdi:information-outline"
                  sx={{ fontSize: '16px', verticalAlign: 'middle', color: 'warning.main' }}
                />
              </span>
            </Tooltip>
          )}
        </Typography>
        <Typography
          style={{
            display: 'flex',
            justifyContent: 'left',
            paddingRight: '2px',
            color: '#212B36',
            marginTop: '5px',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );

    const htmlMessageGLAssignFailure = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, display: 'inline' }}>
          Failed to assign GL codes.
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
          {logEntry.extraInfo}
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            paddingRight: '2px',
            justifyContent: 'left',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageGLAssignSuccess = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, display: 'inline' }}>
          GL codes assigned successfully.
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
          {logEntry.extraInfo}
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            paddingRight: '2px',
            justifyContent: 'left',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageInvoiceParsingCanceled = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, display: 'inline' }}>
          Parsing process was canceled by <CopyTooltip user={logEntry?.user} />
        </Typography>

        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            paddingRight: '2px',
            justifyContent: 'left',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageInvoiceParseIssue = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, display: 'inline' }}>
          BobAI Parsing Issue
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
          {logEntry.extraInfo}
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            paddingRight: '2px',
            justifyContent: 'left',
            alignItems: 'top',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const invoiceQueuedHtml = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, paddingRight: '2px' }}>
          Invoice was added to the queue by <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            justifyContent: 'left',
            alignItems: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );
    const htmlMessageSplitPDF = (
      <>
        <Typography color="info.main" sx={{ fontSize: '14px', fontWeight: 600, paddingRight: '2px' }}>
          {logEntry.extraInfo === 'New'
            ? 'Invoice was created from a split PDF by '
            : 'Invoice PDF was split by '}
          <CopyTooltip user={logEntry?.user} />
        </Typography>
        <Typography
          style={{
            display: 'flex',
            color: '#212B36',
            marginTop: '5px',
            justifyContent: 'left',
            alignItems: 'center',
            fontSize: '14px',
          }}
        >
          {styleDate(logEntry?.dateAdded)}
        </Typography>
      </>
    );

    return logEntry.messageType === 'NewInvoice'
      ? invoiceAddedHtml
      : logEntry.messageType === 'View'
      ? invoiceViewedHtml
      : logEntry.messageType === 'Reject'
      ? invoiceRejectedHtml
      : logEntry.messageType === 'Approve'
      ? htmlMessageInvoiceApproval
      : logEntry.messageType === 'UpdateApprovers'
      ? htmlMessageUpdateApprovers
      : logEntry.messageType === 'MarkAsSent'
      ? htmlMessageMarkAsSent
      : logEntry.messageType === 'NoChargeMarkedSent'
      ? htmlMessageNoChargeMarkedSent
      : logEntry.messageType === 'UpdateOrdersOnly'
      ? htmlMessageUpdateOrdersOnly
      : logEntry.messageType === 'Submit'
      ? htmlMessageSubmissions
      : logEntry.messageType === 'SubmitReview'
      ? htmlMessageSubmitReview
      : logEntry.messageType === 'Download'
      ? htmlMessageDownload
      : logEntry.messageType === 'ItemUpdate'
      ? htmlMessageUpdate
      : logEntry.messageType === 'ItemDelete'
      ? htmlMessageDelete
      : logEntry.messageType === 'File'
      ? htmlMessageFile
      : logEntry.messageType === 'GLAssignFailure'
      ? htmlMessageGLAssignFailure
      : logEntry.messageType === 'GLAssignSuccess'
      ? htmlMessageGLAssignSuccess
      : logEntry.messageType === 'BookingFailed'
      ? bookingFailedHtml
      : logEntry.messageType === 'ParsingCanceled'
      ? htmlMessageInvoiceParsingCanceled
      : logEntry.messageType === 'BobAIParsingIssue'
      ? htmlMessageInvoiceParseIssue
      : logEntry.messageType === 'Queue'
      ? invoiceQueuedHtml
      : logEntry.messageType === 'SubmitAnyway'
      ? htmlMessageSubmitAnyway
      : logEntry.messageType === 'SplitPDF'
      ? htmlMessageSplitPDF
      : '';
  };

  // Text users see only in tooltips (user emails, rejection reasons, full booking
  // failure messages, duplicate warnings), added as search-only text so it is still searchable.
  const getHiddenSearchText = (logEntry) => {
    const parts = [logEntry?.user?.email, logEntry?.fileName, logEntry?.name];
    if (['Reject', 'SubmitAnyway'].includes(logEntry?.messageType)) parts.push(logEntry.extraInfo);
    if (logEntry?.messageType === 'BookingFailed') {
      try {
        parts.push(JSON.parse(logEntry.extraInfo)?.message);
      } catch {
        // not JSON - nothing extra to add
      }
    }
    return parts.filter((part) => typeof part === 'string' && part);
  };

  const typeFilteredLogs = filterType ? mergedArray.filter((logEntry) => logEntry.messageType === filterType) : mergedArray;
  const { containerRef: logContainerRef, isHidden, matchCount } = useLogSearch(searchQuery);

  return (
    <Box
      ref={containerRef}
      sx={{
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        padding: '15px',
        pt: 0,
        borderRadius: '4px',
        boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
        backgroundColor: '#fff',
        border: 'solid 1px #c1c9d0',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', pl: 1, pr: 0, pt: 1 }}>
        {tabs}
        <Stack direction="row" sx={{ ml: 'auto', alignItems: 'center', gap: 0 }}>
          <Box>
            <IconButton
              color="info"
              onClick={handleActionsClick}
              aria-label="More actions"
              sx={{
                minWidth: 0,
                alignItems: 'center',
                borderRadius: '50%',
                p: '2px',
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
              <Iconify icon="mdi:more-vert" />
            </IconButton>
            <Menu
              anchorEl={actionsAnchorEl}
              open={Boolean(actionsAnchorEl)}
              onClose={() => {
                handleActionsClose();
                setIsFilterExpanded(false);
              }}
              slotProps={{
                paper: {
                  sx: {
                    width: 198,
                    p: 1,
                    borderRadius: '5px',
                    boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                  },
                },
                list: {
                  sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                    p: 0,
                  },
                },
              }}
            >
              {isNarrow && (
                <MenuItem
                  disableRipple
                  disableGutters
                  sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
                  onClick={() => {
                    handleSearchToggle();
                    handleActionsClose();
                  }}
                >
                  <DropdownAction badgeIcon="mdi:magnify">Search</DropdownAction>
                </MenuItem>
              )}

              <DropdownExpandableAction
                icon="mdi:filter-outline"
                onExpandedChange={setIsFilterExpanded}
                panel={
                  <Stack spacing={1.5} sx={{ pl: 4.75 }}>
                    <Box
                      onClick={() => {
                        setFilterType('File');
                        handleActionsClose();
                        setIsFilterExpanded(false);
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        color: 'grey.700',
                        '&:hover': { color: 'text.primary' },
                      }}
                    >
                      Attachments
                    </Box>
                    <Box
                      onClick={() => {
                        setFilterType(null);
                        handleActionsClose();
                        setIsFilterExpanded(false);
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        color: 'grey.700',
                        '&:hover': { color: 'text.primary' },
                      }}
                    >
                      All
                    </Box>
                  </Stack>
                }
              >
                Filter
              </DropdownExpandableAction>

              {isFilterExpanded && <Divider sx={{ my: 0.5, mx: 2 }} />}

              {invoiceItemData?.invoice?.sourceMethod !== 'Manual' && (
                <MenuItem
                  disableRipple
                  disableGutters
                  sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
                  disabled={!isEditable}
                  onClick={() => {
                    generatePDF();
                    handleActionsClose();
                  }}
                >
                  <DropdownAction badgeIcon="mdi:file-pdf-box">Generate PDF</DropdownAction>
                </MenuItem>
              )}

              <MenuItem
                disableRipple
                disableGutters
                sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
                disabled={!isEditable}
                onClick={() => {
                  openDropzone();
                  handleActionsClose();
                }}
              >
                <DropdownAction badgeIcon="mdi:tray-upload">Upload File</DropdownAction>
              </MenuItem>
              <MenuItem
              disableRipple
              disableGutters
              sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
              onClick={() => {
                handleLogDownload();
                handleActionsClose();
              }}
            >
              <DropdownAction badgeIcon="mdi:tray-download" >
                Download
              </DropdownAction>
            </MenuItem>
            <MenuItem
              disableRipple
              disableGutters
              sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
              onClick={() => {
                handleLogPrint();
                handleActionsClose();
              }}
            >
              <DropdownAction badgeIcon="mdi:printer">
                Print
              </DropdownAction>
            </MenuItem>
          </Menu>
          </Box>
          {!isNarrow && (
            <Tooltip title="Search">
              <IconButton
                color="info"
                onClick={handleSearchToggle}
                aria-label="Search log"
                sx={{
                  minWidth: 0,
                  alignItems: 'center',
                  borderRadius: '50%',
                  p: '2px',
                  '&:hover': { bgcolor: 'transparent' },
                }}
              >
                <Iconify icon="mdi:magnify" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={`${expand ? 'less' : 'more'}`}>
            <IconButton
              onClick={handleToggleExpand}
              sx={{
                color: 'info.main',
                borderRadius: '50%',
                p: '2px',
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
              <Iconify icon={`ic:twotone-expand-${expand ? 'less' : 'more'}`} width={26} height={26} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {showSearchBar && (
        <Box sx={{ px: 1, pt: 1, pb: 1 }}>
          <LogSearchBar
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClose={handleSearchToggle}
          />
        </Box>
      )}

      {expand && searchQuery.trim() && matchCount === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
          No matching log items
        </Typography>
      )}

      {expand && (
        <Scrollbar sx={{ flex: 1, height: logHeight }}>
          <Box ref={logContainerRef} className="logContainer" sx={{ position: 'relative', overflowX: 'hidden', overflowY: 'hidden' }}>
            <div
              className="logLine"
              style={{
                position: 'absolute',
                zIndex: 0,
                left: '-6px',
                width: '1px',
                backgroundColor: '#208ee078',
                margin: '20px',
              }}
            />
            {(() => {
            // idx drives the first entry's top margin, so count only entries left visible.
            let visibleIdx = -1;
            return typeFilteredLogs.map((logEntry, idx) => {
              const logObject =
                logEntry.messageType === 'NewInvoice'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'formkit:upload', logEntry }
                  : logEntry.messageType === 'View'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'hugeicons:view', logEntry }
                  : logEntry.messageType === 'Reject'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'fluent:text-change-reject-20-filled', logEntry }
                  : logEntry.messageType === 'Approve'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:approve', logEntry }
                  : logEntry.messageType === 'UpdateApprovers'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:approve', logEntry }
                  : logEntry.messageType === 'Submit'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'tabler:send', logEntry }
                  : logEntry.messageType === 'SubmitReview'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'tabler:send', logEntry }
                  : logEntry.messageType === 'Download'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'lucide:download', logEntry }
                  : logEntry.messageType === 'ItemUpdate'
                  ? { htmlMessage: getHtmlMessage(logEntry, setHighlightItem), icon: 'clarity:update-line', logEntry }
                  : logEntry.messageType === 'MarkAsSent'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'hugeicons:sent-02', logEntry }
                  : logEntry.messageType === 'NoChargeMarkedSent'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'hugeicons:sent-02', logEntry }
                  : logEntry.messageType === 'UpdateOrdersOnly'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'hugeicons:sent-02', logEntry }
                  : logEntry.messageType === 'ItemDelete'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'material-symbols:delete-outline-rounded', logEntry }
                  : logEntry.messageType === 'BookingFailed' && getHtmlMessage(logEntry) !== null
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:error', logEntry }
                  : // do not show intacct data files as their own log entry; show together with submit log entry
                  logEntry.messageType === 'File' &&
                    logEntry.invoiceAttachmentCategoryId !== InvoiceAttachmentCategories.IntacctDataFile
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'ph:file-duotone', logEntry }
                  : logEntry.messageType === 'GLAssignFailure'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:alert-circle-outline', logEntry }
                  : logEntry.messageType === 'GLAssignSuccess'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:check-circle-outline', logEntry }
                  : logEntry.messageType === 'ParsingCanceled'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:cancel', logEntry }
                  : logEntry.messageType === 'BobAIParsingIssue'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:alert-circle-outline', logEntry }
                  : logEntry.messageType === 'Queue'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:progress-clock', logEntry }
                  : logEntry.messageType === 'SubmitAnyway'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'humbleicons:duplicate', logEntry }
                  : logEntry.messageType === 'SplitPDF'
                  ? { htmlMessage: getHtmlMessage(logEntry), icon: 'mdi:call-split', logEntry }
                  : '';

              if (logObject === '') return '';
              const key = `${logEntry.messageType}-${logEntry.id ?? idx}`;
              const hidden = isHidden(key);
              if (!hidden) visibleIdx += 1;
              return (
                <LogSearchEntry
                  key={key}
                  entryKey={key}
                  hidden={hidden}
                  date={logEntry.dateAdded}
                  searchText={getHiddenSearchText(logEntry)}
                >
                  <LogTemplate icon={logObject?.icon} message={logObject?.htmlMessage} idx={Math.max(visibleIdx, 0)} />
                </LogSearchEntry>
              );
            });
            })()}
          </Box>
        </Scrollbar>
      )}
    </Box>
  );
};

const CopyTooltip = (user) => {
  const handleCopyToClipboard = (data) => {
    navigator.clipboard
      .writeText(data)
      .then(() => {
        enqueueGlobalSnackbar('Email copied to clipboard!', {
          variant: 'info',
        });
      })
      .catch((err) => {
        console.error('Failed to copy email: ', err);
      });
  };
  return (
    <Tooltip
      onClick={() => handleCopyToClipboard(user?.user?.email)}
      title={
        <Stack direction={'row'}>
          <Icon icon="ph:copy-bold" width="1rem" height="1rem" /> &nbsp;{user?.user?.email}
        </Stack>
      }
      arrow
    >
      <span>@{user?.user?.fullName}</span>
    </Tooltip>
  );
};
